"use client";
import { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { createPurchase, reset } from "@/state/purchases/purchase.slice";
import { getSuppliers } from "@/state/suppliers/supplier.slice";
import { fetchBusiness } from "@/state/business/business.slice";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Loader2, Plus, Trash2, ArrowLeft, Search } from "lucide-react";
import { useToast } from "@/components/ui/use-toast";
import { Link } from "react-router-dom";

const API_URL = import.meta.env.VITE_API_URL;

export default function AddPurchasePage() {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { toast } = useToast();
    const { loading, success, error, message } = useSelector((state) => state.purchases);
    const supplierState = useSelector((state) => state.supplier || {});
    const suppliers = Array.isArray(supplierState.suppliers) ? supplierState.suppliers : supplierState.suppliers?.data || [];
    const { business } = useSelector((state) => state.business || {});

    // Form state
    const [formData, setFormData] = useState({
        supplier_id: "",
        invoice_number: "",
        purchase_date: new Date().toISOString().split("T")[0],
        purchase_status: "received",
        due_date: "",
        discount_type: "none",
        discount_value: 0,
        tax_rate: 0,
        paid_amount: 0,
        payment_method: "Cash",
        notes: "",
    });

    const [items, setItems] = useState([]);
    const [searchTerm, setSearchTerm] = useState("");
    const [searchResults, setSearchResults] = useState([]);
    const [searching, setSearching] = useState(false);

    const currency = business?.currency || "GHS";

    useEffect(() => {
        dispatch(getSuppliers());
        dispatch(fetchBusiness());
    }, [dispatch]);

    useEffect(() => {
        if (success && message) {
            toast({ title: "Success", description: message });
            dispatch(reset());
            navigate("/dashboard/purchases");
        }
        if (error) {
            toast({ variant: "destructive", title: "Error", description: error });
            dispatch(reset());
        }
    }, [success, error, message, toast, dispatch, navigate]);

    // Product search
    const searchProducts = async (term) => {
        if (!term.trim()) {
            setSearchResults([]);
            return;
        }
        setSearching(true);
        try {
            const res = await axios.get(`${API_URL}products?search=${term}&limit=10`, {
                withCredentials: true,
            });
            setSearchResults(res.data?.data?.products || []);
        } catch (err) {
            console.error("Search failed:", err);
        } finally {
            setSearching(false);
        }
    };

    useEffect(() => {
        const timer = setTimeout(() => {
            searchProducts(searchTerm);
        }, 300);
        return () => clearTimeout(timer);
    }, [searchTerm]);

    const addItem = (product) => {
        const exists = items.find((i) => i.product_id === product.id);
        if (exists) {
            setItems(items.map((i) =>
                i.product_id === product.id
                    ? { ...i, quantity: i.quantity + 1 }
                    : i
            ));
        } else {
            const units = product.units || [];
            const base = units.find((u) => u.is_base) || (units.length ? units[0] : null);
            setItems([...items, {
                product_id: product.id,
                product_name: product.title,
                quantity: 1,
                unit_cost: parseFloat(product.buying_price) || 0,
                units,
                unit_id: base ? base.id : null,
            }]);
        }
        setSearchTerm("");
        setSearchResults([]);
    };

    const updateItem = (index, field, value) => {
        setItems(items.map((item, i) =>
            i === index ? { ...item, [field]: value } : item
        ));
    };

    const removeItem = (index) => {
        setItems(items.filter((_, i) => i !== index));
    };

    // Calculations
    const subtotal = items.reduce((sum, item) =>
        sum + (parseFloat(item.unit_cost) || 0) * (parseInt(item.quantity) || 0), 0
    );

    const discountAmount = formData.discount_type === "percentage"
        ? (subtotal * formData.discount_value) / 100
        : formData.discount_type === "fixed"
            ? formData.discount_value
            : 0;

    const taxableAmount = subtotal - discountAmount;
    const taxAmount = formData.tax_rate > 0 ? (taxableAmount * formData.tax_rate) / 100 : 0;
    const grandTotal = taxableAmount + taxAmount;
    const outstanding = Math.max(0, grandTotal - (parseFloat(formData.paid_amount) || 0));

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!formData.supplier_id) {
            toast({ variant: "destructive", title: "Error", description: "Please select a supplier" });
            return;
        }
        if (items.length === 0) {
            toast({ variant: "destructive", title: "Error", description: "Please add at least one item" });
            return;
        }

        dispatch(createPurchase({
            supplier_id: formData.supplier_id,
            invoice_number: formData.invoice_number,
            purchase_date: formData.purchase_date,
            items,
            paid_amount: formData.paid_amount,
            payment_method: formData.payment_method,
            due_date: formData.due_date || null,
            notes: formData.notes,
            discount_type: formData.discount_type,
            discount_value: parseFloat(formData.discount_value) || 0,
            tax_rate: parseFloat(formData.tax_rate) || 0,
            purchase_status: formData.purchase_status,
        }));
    };

    return (
        <div className="mx-auto p-4 space-y-6">
            <div className="flex items-center gap-4">
                <Link to="/dashboard/purchases">
                    <Button variant="ghost" size="icon">
                        <ArrowLeft className="h-5 w-5" />
                    </Button>
                </Link>
                <h1 className="text-3xl font-bold">Add Purchase</h1>
            </div>

            <form
                onSubmit={handleSubmit}
                onKeyDown={(e) => {
                    // Prevent Enter in a single-line input (e.g. the product search,
                    // reference, qty/cost) from submitting the whole purchase. Only the
                    // explicit "Create Purchase" button should submit; textareas keep Enter.
                    if (e.key === "Enter" && e.target.tagName === "INPUT" && e.target.type !== "submit") {
                        e.preventDefault();
                    }
                }}
            >
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    {/* Left Column - Main Form */}
                    <div className="lg:col-span-2 space-y-6">
                        {/* Header Info */}
                        <Card>
                            <CardHeader>
                                <CardTitle>Purchase Details</CardTitle>
                            </CardHeader>
                            <CardContent className="grid grid-cols-2 md:grid-cols-4 gap-4">
                                <div className="col-span-2">
                                    <Label>Supplier *</Label>
                                    <Select
                                        value={formData.supplier_id?.toString() || ""}
                                        onValueChange={(val) => setFormData({ ...formData, supplier_id: val })}
                                    >
                                        <SelectTrigger>
                                            <SelectValue placeholder="Select Supplier" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {suppliers?.map((s) => (
                                                <SelectItem key={s.id} value={s.id.toString()}>
                                                    {s.name}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                                <div>
                                    <Label>Reference No.</Label>
                                    <Input
                                        value={formData.invoice_number}
                                        onChange={(e) => setFormData({ ...formData, invoice_number: e.target.value })}
                                        placeholder="INV-001"
                                    />
                                </div>
                                <div>
                                    <Label>Purchase Date</Label>
                                    <Input
                                        type="date"
                                        value={formData.purchase_date}
                                        onChange={(e) => setFormData({ ...formData, purchase_date: e.target.value })}
                                    />
                                </div>
                                <div>
                                    <Label>Status</Label>
                                    <Select
                                        value={formData.purchase_status}
                                        onValueChange={(val) => setFormData({ ...formData, purchase_status: val })}
                                    >
                                        <SelectTrigger>
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="ordered">Ordered</SelectItem>
                                            <SelectItem value="pending">Pending</SelectItem>
                                            <SelectItem value="received">Received</SelectItem>
                                        </SelectContent>
                                    </Select>
                                </div>
                                <div>
                                    <Label>Due Date (Credit)</Label>
                                    <Input
                                        type="date"
                                        value={formData.due_date}
                                        onChange={(e) => setFormData({ ...formData, due_date: e.target.value })}
                                    />
                                </div>
                            </CardContent>
                        </Card>

                        {/* Product Search & Items */}
                        <Card>
                            <CardHeader>
                                <CardTitle>Products</CardTitle>
                            </CardHeader>
                            <CardContent className="space-y-4">
                                <div className="relative">
                                    <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                                    <Input
                                        placeholder="Search products by name or SKU..."
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                        className="pl-10"
                                    />
                                    {searchResults.length > 0 && (
                                        <div className="absolute z-10 w-full bg-white dark:bg-slate-900 border rounded-md shadow-lg mt-1 max-h-60 overflow-y-auto">
                                            {searchResults.map((product) => (
                                                <div
                                                    key={product.id}
                                                    className="p-3 hover:bg-slate-100 dark:hover:bg-slate-800 cursor-pointer flex justify-between"
                                                    onClick={() => addItem(product)}
                                                >
                                                    <span>{product.title}</span>
                                                    <span className="text-muted-foreground">{currency} {parseFloat(product.buying_price || 0).toFixed(2)}</span>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>

                                {items.length > 0 ? (
                                    <Table>
                                        <TableHeader>
                                            <TableRow>
                                                <TableHead>Product</TableHead>
                                                <TableHead className="w-28">Unit</TableHead>
                                                <TableHead className="w-24">Qty</TableHead>
                                                <TableHead className="w-32">Unit Cost</TableHead>
                                                <TableHead className="w-32">Batch #</TableHead>
                                                <TableHead className="w-40">Expiry</TableHead>
                                                <TableHead className="text-right w-32">Total</TableHead>
                                                <TableHead className="w-12"></TableHead>
                                            </TableRow>
                                        </TableHeader>
                                        <TableBody>
                                            {items.map((item, index) => (
                                                <TableRow key={index}>
                                                    <TableCell>{item.product_name}</TableCell>
                                                    <TableCell>
                                                        {Array.isArray(item.units) && item.units.length > 0 ? (
                                                            <select
                                                                value={item.unit_id ?? ""}
                                                                onChange={(e) => updateItem(index, "unit_id", e.target.value ? Number(e.target.value) : null)}
                                                                className="text-sm border rounded px-1 py-1 bg-background w-24"
                                                            >
                                                                {item.units.map((u) => (
                                                                    <option key={u.id} value={u.id}>
                                                                        {u.name || (u.is_base ? "Base" : `x${u.factor_to_base}`)}
                                                                    </option>
                                                                ))}
                                                            </select>
                                                        ) : (
                                                            <span className="text-xs text-muted-foreground">base</span>
                                                        )}
                                                    </TableCell>
                                                    <TableCell>
                                                        <Input
                                                            type="number"
                                                            min="1"
                                                            value={item.quantity}
                                                            onChange={(e) => updateItem(index, "quantity", parseInt(e.target.value) || 1)}
                                                            className="w-20"
                                                        />
                                                        {(() => {
                                                            const u = (item.units || []).find((x) => String(x.id) === String(item.unit_id));
                                                            const f = u ? parseFloat(u.factor_to_base) || 1 : 1;
                                                            return f > 1 ? (
                                                                <p className="text-[11px] text-muted-foreground mt-0.5">= {(item.quantity || 0) * f} pcs</p>
                                                            ) : null;
                                                        })()}
                                                    </TableCell>
                                                    <TableCell>
                                                        <Input
                                                            type="number"
                                                            step="0.01"
                                                            min="0"
                                                            value={item.unit_cost}
                                                            onChange={(e) => updateItem(index, "unit_cost", parseFloat(e.target.value) || 0)}
                                                            className="w-28"
                                                        />
                                                    </TableCell>
                                                    <TableCell>
                                                        <Input
                                                            type="text"
                                                            placeholder="optional"
                                                            value={item.batch_number || ""}
                                                            onChange={(e) => updateItem(index, "batch_number", e.target.value)}
                                                            className="w-28"
                                                        />
                                                    </TableCell>
                                                    <TableCell>
                                                        <Input
                                                            type="date"
                                                            value={item.expiry_date || ""}
                                                            onChange={(e) => updateItem(index, "expiry_date", e.target.value)}
                                                            className="w-36"
                                                        />
                                                    </TableCell>
                                                    <TableCell className="text-right font-medium">
                                                        {currency} {((item.unit_cost || 0) * (item.quantity || 0)).toFixed(2)}
                                                    </TableCell>
                                                    <TableCell>
                                                        <Button
                                                            type="button"
                                                            variant="ghost"
                                                            size="icon"
                                                            onClick={() => removeItem(index)}
                                                        >
                                                            <Trash2 className="h-4 w-4 text-red-500" />
                                                        </Button>
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                ) : (
                                    <div className="text-center py-8 text-muted-foreground">
                                        Search and add products above
                                    </div>
                                )}
                            </CardContent>
                        </Card>

                        {/* Notes */}
                        <Card>
                            <CardHeader>
                                <CardTitle>Additional Notes</CardTitle>
                            </CardHeader>
                            <CardContent>
                                <Textarea
                                    value={formData.notes}
                                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                                    placeholder="Any additional notes for this purchase..."
                                    rows={3}
                                />
                            </CardContent>
                        </Card>
                    </div>

                    {/* Right Column - Summary */}
                    <div className="space-y-6">
                        {/* Discount & Tax */}
                        <Card>
                            <CardHeader>
                                <CardTitle>Discount & Tax</CardTitle>
                            </CardHeader>
                            <CardContent className="space-y-4">
                                <div className="grid grid-cols-2 gap-2">
                                    <div>
                                        <Label>Discount Type</Label>
                                        <Select
                                            value={formData.discount_type}
                                            onValueChange={(val) => setFormData({ ...formData, discount_type: val })}
                                        >
                                            <SelectTrigger>
                                                <SelectValue />
                                            </SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="none">None</SelectItem>
                                                <SelectItem value="percentage">Percentage (%)</SelectItem>
                                                <SelectItem value="fixed">Fixed Amount</SelectItem>
                                            </SelectContent>
                                        </Select>
                                    </div>
                                    <div>
                                        <Label>Discount Value</Label>
                                        <Input
                                            type="number"
                                            min="0"
                                            step="0.01"
                                            value={formData.discount_value}
                                            onChange={(e) => setFormData({ ...formData, discount_value: parseFloat(e.target.value) || 0 })}
                                            disabled={formData.discount_type === "none"}
                                        />
                                    </div>
                                </div>
                                <div>
                                    <Label>Tax Rate (%)</Label>
                                    <Input
                                        type="number"
                                        min="0"
                                        max="100"
                                        step="0.01"
                                        value={formData.tax_rate}
                                        onChange={(e) => setFormData({ ...formData, tax_rate: parseFloat(e.target.value) || 0 })}
                                    />
                                </div>
                            </CardContent>
                        </Card>

                        {/* Totals */}
                        <Card>
                            <CardHeader>
                                <CardTitle>Summary</CardTitle>
                            </CardHeader>
                            <CardContent className="space-y-3">
                                <div className="flex justify-between">
                                    <span>Subtotal ({items.length} items)</span>
                                    <span>{currency} {subtotal.toFixed(2)}</span>
                                </div>
                                {discountAmount > 0 && (
                                    <div className="flex justify-between text-green-600">
                                        <span>Discount</span>
                                        <span>- {currency} {discountAmount.toFixed(2)}</span>
                                    </div>
                                )}
                                {taxAmount > 0 && (
                                    <div className="flex justify-between">
                                        <span>Tax ({formData.tax_rate}%)</span>
                                        <span>+ {currency} {taxAmount.toFixed(2)}</span>
                                    </div>
                                )}
                                <div className="flex justify-between font-bold text-lg border-t pt-3">
                                    <span>Grand Total</span>
                                    <span>{currency} {grandTotal.toFixed(2)}</span>
                                </div>
                            </CardContent>
                        </Card>

                        {/* Payment */}
                        <Card>
                            <CardHeader>
                                <CardTitle>Payment</CardTitle>
                            </CardHeader>
                            <CardContent className="space-y-4">
                                <div>
                                    <Label>Payment Method</Label>
                                    <Select
                                        value={formData.payment_method}
                                        onValueChange={(val) => setFormData({ ...formData, payment_method: val })}
                                    >
                                        <SelectTrigger>
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="Cash">Cash</SelectItem>
                                            <SelectItem value="Momo">Mobile Money</SelectItem>
                                            <SelectItem value="Bank Transfer">Bank Transfer</SelectItem>
                                            <SelectItem value="Check">Check</SelectItem>
                                        </SelectContent>
                                    </Select>
                                </div>
                                <div>
                                    <Label>Amount Paid ({currency})</Label>
                                    <Input
                                        type="number"
                                        min="0"
                                        step="0.01"
                                        value={formData.paid_amount}
                                        onChange={(e) => setFormData({ ...formData, paid_amount: parseFloat(e.target.value) || 0 })}
                                    />
                                </div>
                                <div className="flex justify-between font-medium">
                                    <span>Outstanding</span>
                                    <span className={outstanding > 0 ? "text-orange-600" : "text-green-600"}>
                                        {currency} {outstanding.toFixed(2)}
                                    </span>
                                </div>
                                {outstanding > 0 && (
                                    <Badge variant="outline" className="w-full justify-center py-2 bg-yellow-50">
                                        Credit Purchase
                                    </Badge>
                                )}
                            </CardContent>
                        </Card>

                        {/* Submit */}
                        <Button type="submit" className="w-full" size="lg" disabled={loading}>
                            {loading ? (
                                <>
                                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                    Creating...
                                </>
                            ) : (
                                <>
                                    <Plus className="mr-2 h-4 w-4" />
                                    Create Purchase
                                </>
                            )}
                        </Button>
                    </div>
                </div>
            </form>
        </div>
    );
}
